import { forwardRef } from "react";
import { Lock } from "lucide-react";
import { cn } from "@/lib/cn";

export interface SwitchProps {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Locked = policy-enforced state (mandatory notifications, email policy A):
   * rendered semi-transparent with not-allowed cursor; never fires changes.
   */
  locked?: boolean;
  /** Visual override for the locked-email policy state (warning tone). */
  tone?: "default" | "warning";
  "aria-label"?: string;
  className?: string;
}

/**
 * D-043 S2 — accessible switch (no shadcn primitive existed in ui/).
 * role="switch" + aria-checked; 38×22 track, 18px thumb (mockup .sw).
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, disabled, locked, tone = "default", className, ...aria }, ref) => {
    const inert = disabled || locked;

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={inert || undefined}
        disabled={disabled}
        onClick={() => {
          if (!inert) onCheckedChange?.(!checked);
        }}
        className={cn(
          "relative inline-flex h-[22px] w-[38px] shrink-0 items-center rounded-full transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          checked
            ? tone === "warning"
              ? "bg-warning-foreground"
              : "bg-primary"
            : "bg-border",
          locked && "cursor-not-allowed opacity-50",
          disabled && !locked && "cursor-not-allowed opacity-40",
          className,
        )}
        {...aria}
      >
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none flex h-[18px] w-[18px] translate-x-[2px] items-center justify-center rounded-full bg-white shadow transition-transform",
            checked && "translate-x-[18px]",
          )}
        >
          {locked && <Lock className="h-2.5 w-2.5 text-muted-foreground" />}
        </span>
      </button>
    );
  },
);
Switch.displayName = "Switch";
